import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  ActivityIndicator,
  Linking,
  Clipboard,
  useWindowDimensions,
  Pressable,
} from 'react-native';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Field,
  SectionCard,
  SectionDivider,
  StatRow,
  StatTile,
} from '@/components/ui/section-card';
import { ThemedText } from '@/components/themed-text';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { Charge } from '@/types/charge';
import { CHARGE_STATUS } from '@/types/charge-status';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { AutomaticPaymentSection } from '@/components/charges/automatic-payment-section';
import { Ionicons } from '@expo/vector-icons';
import api from '@/services/api';

interface PaymentDetailsModalProps {
  charge: Charge | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReportPaymentSuccess?: () => void;
  onOpenProofModal?: () => void;
}

export function PaymentDetailsModal({
  charge,
  open,
  onOpenChange,
  onReportPaymentSuccess,
  onOpenProofModal,
}: PaymentDetailsModalProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { addToast } = useToastStore();
  const [isReporting, setIsReporting] = useState(false);
  const [step, setStep] = useState<'DETAILS' | 'REPORT_OPTIONS'>('DETAILS');

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => setStep('DETAILS'), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!charge) return null;

  const isPaid = charge.status === CHARGE_STATUS.PAID;
  const isReported = charge.status === ('PAYMENT_REPORTED' as any);
  // Cobrança automática: o cliente paga aqui mesmo (cartão, Pix, boleto —
  // o que a cobrança aceitar), no mesmo checkout embutido do `/checkout/:id`.
  const isAutomatic =
    charge.processingMethod === PROCESSING_METHOD.PLATFORM && !isPaid && !isReported;

  const handleCopyPixCode = async () => {
    if (!charge.pixCopyPaste) return;
    await Clipboard.setString(charge.pixCopyPaste);
    addToast('Código Pix copiado!', 'success');
  };

  const handleReportPayment = async () => {
    setIsReporting(true);
    try {
      await api.post(`/charge/${charge.id}/report-payment`);
      addToast('Pagamento informado com sucesso!', 'success');
      onReportPaymentSuccess?.();
    } catch (error: any) {
      addToast(getApiErrorMessage(error, 'Erro ao informar pagamento'), 'error');
    } finally {
      setIsReporting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Layout responsivo seguindo EXATAMENTE o manual-charge-modal.tsx
  const contentClassName = isMobile
    ? 'w-full h-full rounded-none'
    : 'w-full min-w-[500px]  max-h-[90vh]';

  return (
    <Dialog open={open} onOpenChange={(val) => !isReporting && onOpenChange(val)}>
      <DialogContent className={contentClassName}>
        {/* Botão de fechar (X) - Padrão manual-charge-modal.tsx */}
        <Pressable
          onPress={handleClose}
          className="absolute right-4 top-4 z-10 rounded-full p-2 hover:bg-neutral-100"
        >
          <Ionicons className="text-neutral-500" name="close" size={24} />
        </Pressable>

        <DialogHeader className="pr-10">
          <DialogTitle>
            <ThemedText className="font-bold text-xl">
              {step === 'DETAILS' ? 'Detalhes do pagamento' : 'Informar pagamento'}
            </ThemedText>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="text-sm text-foreground/60">
              {step === 'DETAILS'
                ? isPaid
                  ? 'Esta cobrança já foi paga.'
                  : isAutomatic
                    ? 'Pague aqui mesmo, sem sair do app.'
                    : 'Informações para realizar o pagamento manual.'
                : 'Como você deseja informar este pagamento?'}
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          className={isMobile ? 'mt-4 flex-1' : 'mt-4 max-h-[50vh]'}
          showsVerticalScrollIndicator={false}
        >
          <View className="mt-4 gap-4 px-1 pb-4">
            {step === 'DETAILS' ? (
              <>
                {/* Status Feedback */}
                {isPaid && (
                  <View className="rounded-lg border border-green-200 bg-green-50 p-4">
                    <View className="flex-row items-center gap-2">
                      <Ionicons className="text-success-600" name="checkmark-circle" size={20} />
                      <ThemedText className="font-medium text-sm text-green-800">
                        Pagamento confirmado em {formatDate(charge.paidAt!)}
                      </ThemedText>
                    </View>
                  </View>
                )}

                {isReported && !isPaid && (
                  <View className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <View className="flex-row items-center gap-2">
                      <Ionicons className="text-info-600" name="information-circle" size={20} />
                      <ThemedText className="font-medium text-sm text-blue-800">
                        Pagamento informado. Aguarde a confirmação.
                      </ThemedText>
                    </View>
                  </View>
                )}

                {/* O que está sendo cobrado */}
                <SectionCard icon="document-text-outline" title="Esta cobrança">
                  <StatRow>
                    <StatTile label="Valor" value={formatCurrency(charge.amount)} emphasis />
                    <StatTile label="Vencimento" value={formatDate(charge.dueDate)} />
                  </StatRow>

                  <SectionDivider />

                  <Field
                    label="Serviço"
                    value={charge.serviceName ?? charge.enrollment?.service?.name}
                    emptyText="Vários"
                  />

                  {charge.description ? (
                    <Field label="Descrição" value={charge.description} />
                  ) : null}
                </SectionCard>

                {isAutomatic && (
                  <SectionCard icon="card-outline" title="Pagamento">
                    <AutomaticPaymentSection chargeId={charge.id} onPaid={onReportPaymentSuccess} />
                  </SectionCard>
                )}

                {!isPaid && !isAutomatic && (
                  <SectionCard icon="card-outline" title="Como pagar">
                    {charge.pixCopyPaste && (
                      <View className="gap-2">
                        <ThemedText className="text-xs text-neutral-500">
                          Pix copia e cola
                        </ThemedText>
                        <View className="rounded-2xl bg-neutral-50 p-3">
                          <ThemedText className="font-mono text-xs" numberOfLines={3}>
                            {charge.pixCopyPaste}
                          </ThemedText>
                        </View>
                        <Button
                          variant="outline"
                          onPress={handleCopyPixCode}
                          className="mt-1 rounded-2xl"
                        >
                          <View className="flex-row items-center gap-2">
                            <Ionicons className="text-foreground" name="copy-outline" size={18} />
                            <ThemedText>Copiar código Pix</ThemedText>
                          </View>
                        </Button>
                      </View>
                    )}

                    {charge.paymentLink && (
                      <View className="gap-2">
                        <ThemedText className="text-xs text-neutral-500">
                          Link de pagamento
                        </ThemedText>
                        <Button
                          variant="outline"
                          className="rounded-2xl"
                          onPress={() => Linking.openURL(charge.paymentLink!)}
                        >
                          <View className="flex-row items-center gap-2">
                            <Ionicons className="text-foreground" name="open-outline" size={18} />
                            <ThemedText>Abrir link externo</ThemedText>
                          </View>
                        </Button>
                      </View>
                    )}

                    {(charge.bankName || charge.bankAccount) && (
                      <View className="gap-3">
                        <SectionDivider />
                        <Field label="Banco" value={charge.bankName} />
                        <StatRow>
                          <StatTile label="Agência" value={charge.bankAgency || '—'} />
                          <StatTile label="Conta" value={charge.bankAccount || '—'} />
                        </StatRow>
                      </View>
                    )}

                    {charge.paymentInstructions ? (
                      <View className="rounded-2xl bg-amber-50 p-3">
                        <ThemedText className="text-xs text-amber-700">
                          Instruções do prestador
                        </ThemedText>
                        <ThemedText className="text-sm leading-5 text-amber-800">
                          {charge.paymentInstructions}
                        </ThemedText>
                      </View>
                    ) : null}
                  </SectionCard>
                )}
              </>
            ) : (
              <View className="gap-4 py-4">
                <Button
                  className="h-16 flex-row items-center justify-start gap-3 rounded-xl border border-primary bg-primary px-4"
                  onPress={() => onOpenProofModal?.()}
                >
                  <View className="rounded-full bg-white/20 p-2">
                    <Ionicons name="cloud-upload" size={24} color="white" />
                  </View>
                  <View className="flex-1">
                    <ThemedText className="font-bold text-base text-white">
                      Anexe o comprovante de pagamento
                    </ThemedText>
                    <ThemedText className="text-xs text-white/80">
                      Recomendado para agilizar o processamento
                    </ThemedText>
                  </View>
                </Button>

                <Button
                  variant="outline"
                  className="group h-16 flex-row items-center justify-start gap-3 rounded-xl border-2 border-dashed border-primary px-4 hover:bg-primary"
                  onPress={handleReportPayment}
                  disabled={isReporting}
                >
                  <View className="rounded-full bg-neutral-100 p-2 group-hover:bg-white/20">
                    {isReporting ? (
                      <ActivityIndicator
                        size="small"
                        color="#FD5001"
                        className="group-hover:hidden"
                      />
                    ) : (
                      <Ionicons
                        name="checkmark-done"
                        size={24}
                        className="text-primary group-hover:text-white"
                      />
                    )}
                    {isReporting && (
                      <ActivityIndicator
                        size="small"
                        color="white"
                        className="hidden group-hover:flex"
                      />
                    )}
                  </View>
                  <View className="flex-1">
                    <ThemedText className="font-bold text-base text-primary group-hover:text-white">
                      {isReporting ? 'Informando...' : 'Confirmar sem comprovante'}
                    </ThemedText>
                    <ThemedText className="text-xs text-primary/60 group-hover:text-white">
                      {isReporting ? 'Aguarde um momento' : 'Informar pagamento apenas com aviso'}
                    </ThemedText>
                  </View>
                </Button>
              </View>
            )}
          </View>
        </ScrollView>

        <DialogFooter className="mt-6 gap-2">
          {step === 'DETAILS' ? (
            <>
              <Button variant="outline" onPress={handleClose} className="flex-1 sm:flex-none">
                <ThemedText>Fechar</ThemedText>
              </Button>
              {!isPaid && !isReported && !isAutomatic && (
                <Button onPress={() => setStep('REPORT_OPTIONS')} className="flex-1 sm:flex-none">
                  <ThemedText className="text-white">Já realizei o pagamento</ThemedText>
                </Button>
              )}
            </>
          ) : (
            <Button variant="outline" onPress={() => setStep('DETAILS')} className="flex-1">
              <View className="flex-row items-center gap-2">
                <Ionicons className="text-foreground" name="arrow-back" size={18} />
                <ThemedText>Voltar</ThemedText>
              </View>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
